import React, { useMemo, useRef, useState } from 'react';

const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Библиотека ассетов: картинки для писем. Удалить можно только неиспользуемые —
// на картинку ссылаются шаблоны и уже отправленные письма.
const EmailAssets = ({ initialAssets }) => {
  const [assets, setAssets] = useState(initialAssets || []);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState(null);
  const [copied, setCopied] = useState(null);
  const inputRef = useRef(null);

  const visible = useMemo(
    () => assets.filter((a) => !search || a.name.toLowerCase().includes(search.toLowerCase())),
    [assets, search]
  );

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    setNotice(null);
    const form = new FormData();
    [...files].forEach((f) => form.append('files[]', f));
    try {
      const res = await fetch('/email_assets', { method: 'POST', headers: { 'X-CSRF-Token': csrfToken() }, body: form });
      const data = await res.json();
      setAssets((prev) => [...(data.assets || []), ...prev]);
      if (data.errors?.length) setNotice({ error: data.errors.join(' · ') });
      else setNotice({ ok: `Uploaded ${data.assets.length} image${data.assets.length === 1 ? '' : 's'}` });
    } catch (e) {
      setNotice({ error: e.message });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (asset) => {
    if (!window.confirm(`Delete “${asset.name}”?`)) return;
    const res = await fetch(`/email_assets/${asset.id}`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken() } });
    const data = await res.json();
    if (res.ok) {
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
      return;
    }
    const names = [
      ...(data.usage?.templates || []).map((t) => `template “${t.name}”`),
      ...(data.usage?.campaigns || []).map((c) => `campaign “${c.name}”`)
    ];
    setNotice({ error: `“${asset.name}” cannot be deleted — it is used in ${names.join(', ')}.` });
  };

  const copy = async (asset) => {
    try {
      await navigator.clipboard.writeText(asset.url);
      setCopied(asset.id);
      setTimeout(() => setCopied(null), 1500);
    } catch (_) {
      setNotice({ error: 'Copy failed — select the link manually' });
    }
  };

  return (
    <div className='min-h-screen bg-slate-50/50'>
      <div className='max-w-[1400px] mx-auto px-6 py-8'>
        <div className='flex items-center justify-between mb-6'>
          <div>
            <h1 className='text-2xl font-semibold text-slate-900'>Assets</h1>
            <p className='mt-1 text-sm text-slate-600'>
              Images for email templates. They are also available right inside the email editor.
            </p>
          </div>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className='px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 rounded-lg shadow-sm disabled:opacity-50'
          >
            {busy ? 'Uploading…' : 'Upload images'}
          </button>
          <input ref={inputRef} type='file' accept='image/png,image/jpeg,image/gif,image/webp' multiple className='hidden' onChange={(e) => upload(e.target.files)} />
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
          className={`mb-6 border-2 border-dashed rounded-xl px-6 py-6 text-center text-sm transition-colors ${
            dragging ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-500'
          }`}
        >
          Drop PNG, JPEG, GIF or WebP files here (up to 5 MB each)
        </div>

        <div className='flex items-center gap-4 mb-4'>
          <input
            className='shadow-sm border border-slate-300 rounded px-3 py-2 text-sm w-80'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search by name…'
          />
          <span className='text-xs text-slate-500 uppercase'>{visible.length} image{visible.length === 1 ? '' : 's'}</span>
        </div>

        {notice?.ok && <div className='mb-4 text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2'>{notice.ok}</div>}
        {notice?.error && <div className='mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2'>{notice.error}</div>}

        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4'>
          {visible.map((asset) => (
            <div key={asset.id} className='bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm group'>
              <div className='aspect-square bg-slate-100 flex items-center justify-center overflow-hidden'
                   style={{ backgroundImage: 'linear-gradient(45deg,#e2e8f0 25%,transparent 25%),linear-gradient(-45deg,#e2e8f0 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e2e8f0 75%),linear-gradient(-45deg,transparent 75%,#e2e8f0 75%)', backgroundSize: '16px 16px', backgroundPosition: '0 0,0 8px,8px -8px,-8px 0' }}>
                <img src={asset.url} alt={asset.name} className='max-w-full max-h-full object-contain' loading='lazy' />
              </div>
              <div className='p-2'>
                <div className='text-sm text-slate-900 truncate' title={asset.name}>{asset.name}</div>
                <div className='text-xs text-slate-400'>{formatSize(asset.byte_size)} · {new Date(asset.created_at).toLocaleDateString()}</div>
                <div className='mt-1 flex items-center justify-between'>
                  <button onClick={() => copy(asset)} className='text-xs text-blue-600 hover:text-blue-800'>
                    {copied === asset.id ? 'Copied' : 'Copy link'}
                  </button>
                  <button onClick={() => remove(asset)} className='text-xs text-red-600 hover:text-red-800'>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {visible.length === 0 && (
          <div className='py-16 text-center text-sm text-slate-500'>
            {assets.length ? 'Nothing matches the search.' : 'No images yet — upload the first one.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailAssets;
